<template>
    <div class="lsva-truck-edit l-stack l-gap-3">
        <div class="l-inline l-center-v l-gap-2">
            <BaseAvatar :src="truck.marker" :color="truck.color" :size="32" />
            <h3>{{ truck.name }}</h3>
        </div>
        <div class="l-stack l-gap-2">
            <BaseInput
                v-model="truckForm.vin"
                :placeholder="$t('VIN')"
                :maxlength="17"
                @input="removeSpaces"
            />

            <DateInput
                v-model="truckForm.end_date"
                :placeholder="$t('registrationEnd')"
                :disabled-dates="registrationEndLimit"
            />
        </div>

        <div class="l-inline l-spread l-center-v">
            <span class="clickable" @click="cancel">
                {{ $t('shared.cancel') }}
            </span>

            <BaseButton
                :disabled="!isFormValid"
                :is-loading="isTruckPending"
                size="small"
                @click="edit"
            >
                {{ $t('shared.save') }}
            </BaseButton>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import moment from 'moment'

import { httpHelper } from '@/utils'
import BaseAvatar from '@/components/base/BaseAvatar'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseInput from '@/components/redesigned/BaseInput'
import DateInput from '@/components/redesigned/DateInput'

const DEFAULT_TRUCK = {
    vin: '',
}

export default {
    name: 'LsvaTruckEdit',
    components: {
        BaseAvatar,
        BaseButton,
        BaseInput,
        DateInput,
    },
    props: {
        truck: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            truckForm: { ...this.truck },
        }
    },
    computed: {
        ...mapState('lsva', ['isTruckPending']),
        isFormValid() {
            return this.truckForm.vin.length === 17
        },
        registrationEndLimit() {
            return {
                to: moment()
                    .subtract(1, 'day')
                    .endOf('day')
                    .toDate(),
            }
        },
    },
    methods: {
        ...mapActions('lsva', ['updateTruck']),
        async edit() {
            if (!this.isFormValid) {
                return
            }

            if (this.truck.vin === this.truckForm.vin) {
                delete this.truckForm.vin
            }

            if (this.truck.end_date === this.truckForm.end_date) {
                delete this.truckForm.end_date
            }

            const successNotification = {
                title: this.$t('shared.success'),
                text: this.$t('truckUpdated'),
            }
            const errorTitle = this.$t('shared.error')

            try {
                await this.updateTruck(this.truckForm)

                this.$notify(successNotification)
            } catch (e) {
                this.$notify({
                    title: errorTitle,
                    text: httpHelper.parseError(e),
                })
            }

            this.cleanForm()
            this.$emit('close')
        },
        cancel() {
            this.cleanForm()
            this.$emit('close')
        },
        cleanForm() {
            this.truckForm = { ...DEFAULT_TRUCK }
        },
        removeSpaces() {
            this.truckForm.vin = this.truckForm.vin.replace(/\s+/g, '')
        },
    },
}
</script>

<i18n>
{
    "en": {
        "VIN": "VIN",
        "registrationEnd": "End of Registration",
        "truckUpdated": "Truck updated"
    },
    "de": {
        "VIN": "VIN",
        "registrationEnd": "Registrierungsende",
        "truckUpdated": "Lastwagen aktualisiert"
    },
    "fr": {
        "VIN": "VIN",
        "registrationEnd": "Fin d'enregistrement",
        "truckUpdated": "Camion mis à jour"
    },
    "it": {
        "VIN": "VIN",
        "registrationEnd": "Fine registrazione",
        "truckUpdated": "Camion aggiornato"
    }
}
</i18n>

<style lang="scss">
.lsva-truck-edit {
    .clickable {
        font-size: 14px;
    }
}
</style>
